import React, { useEffect, useState } from 'react';
import MainLayout from '../../components/layout/MainLayout';
import PageLoader from "../../components/common/PageLoader";
import api from '../../services/api';
import Swal from 'sweetalert2';
import './Salary.css';

const RUPEE_SYMBOL = '\u20B9';

function SalaryAdvanceSettings() {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [savedData, setSavedData] = useState(null);

    const [formData, setFormData] = useState({
        max_loan_amount: '',
        max_tenure_months: '6',
        interest_rate: '0'
    });

    const [errors, setErrors] = useState({});

    useEffect(() => {
        fetchSettings();
    }, []);

    const fetchSettings = async () => {
        try {
            const response = await api.get('/salary-advance-settings');
            if (response.data.success) {
                const data = response.data.data;
                setSavedData(data);
                setFormData({
                    max_loan_amount: data.max_loan_amount ?? '',
                    max_tenure_months: data.max_tenure_months ?? '6',
                    interest_rate: data.interest_rate ?? '0'
                });
            }
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to load salary advance settings'
            });
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (event) => {
        const { name, value } = event.target;
        setFormData((prev) => ({ ...prev, [name]: value }));

        if (errors[name]) {
            setErrors((prev) => ({ ...prev, [name]: '' }));
        }
    };

    const validate = () => {
        const newErrors = {};

        if (!formData.max_loan_amount || parseFloat(formData.max_loan_amount) < 1) {
            newErrors.max_loan_amount = `Maximum loan amount must be at least ${RUPEE_SYMBOL}1`;
        }

        const tenure = parseInt(formData.max_tenure_months, 10);
        if (!formData.max_tenure_months || tenure < 1 || tenure > 6) {
            newErrors.max_tenure_months = 'Tenure must be between 1 and 6 months';
        }

        if (
            formData.interest_rate !== '' &&
            formData.interest_rate !== null &&
            (parseFloat(formData.interest_rate) < 0 || parseFloat(formData.interest_rate) > 100)
        ) {
            newErrors.interest_rate = 'Interest rate must be between 0% and 100%';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!validate()) return;

        setSaving(true);
        try {
            const response = await api.put('/salary-advance-settings', formData);
            if (response.data.success) {
                Swal.fire({
                    icon: 'success',
                    title: 'Updated!',
                    text: response.data.message,
                    timer: 2000,
                    showConfirmButton: false
                });
                setSavedData({ ...formData });
            }
        } catch (error) {
            const serverErrors = error.response?.data?.errors || {};
            if (Object.keys(serverErrors).length > 0) {
                const mapped = {};
                Object.keys(serverErrors).forEach((key) => {
                    mapped[key] = serverErrors[key][0];
                });
                setErrors(mapped);
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: error.response?.data?.message || 'Failed to update settings'
                });
            }
        } finally {
            setSaving(false);
        }
    };

    const calcEmi = () => {
        const amount = parseFloat(formData.max_loan_amount);
        const months = parseInt(formData.max_tenure_months, 10);
        const rate = parseFloat(formData.interest_rate) || 0;
        if (!amount || !months || amount <= 0 || months <= 0) return null;

        if (rate === 0) {
            return { simple: (amount / months).toFixed(2), total: amount.toFixed(2) };
        }

        const interest = (amount * rate * (months / 12)) / 100;
        const totalPayable = amount + interest;
        const emi = totalPayable / months;

        return {
            simple: emi.toFixed(2),
            total: totalPayable.toFixed(2),
            interest: interest.toFixed(2)
        };
    };

    const emiData = calcEmi();

    if (loading) {
    return <MainLayout><PageLoader force copy="Loading settings..." /></MainLayout>;
  }

    return (
        <MainLayout>
            <div className="sas-container">
                <div className="sas-page-intro">
                    <div>
                        <span className="salary-advances-eyebrow">Policy settings</span>
                        <h1>Salary Advance Settings</h1>
                        <p>
                            Define the maximum eligible amount, repayment window,
                            and interest policy for employee salary advances.
                        </p>
                    </div>
                    <div className="sas-intro-badge">
                        <span>Policy status</span>
                        <strong>{savedData ? 'Configured' : 'Pending setup'}</strong>
                    </div>
                </div>

                <div className="sas-grid">
                    <div className="sas-summary-card">
                        <div className="sas-card-header">
                            <div className="sas-header-icon">
                                <i className="fas fa-hand-holding-usd"></i>
                            </div>
                            <div>
                                <h5>Current Policy</h5>
                                <p>Active loan configuration</p>
                            </div>
                        </div>

                        <div className="sas-summary-body">
                            <SummaryItem
                                icon="fa-rupee-sign"
                                label="Max Loan Amount"
                                value={savedData?.max_loan_amount
                                    ? `${RUPEE_SYMBOL}${parseFloat(savedData.max_loan_amount).toLocaleString('en-IN')}`
                                    : 'Not set'}
                                color="#6557ff"
                            />
                            <SummaryItem
                                icon="fa-calendar-alt"
                                label="Max Tenure"
                                value={savedData?.max_tenure_months
                                    ? `${savedData.max_tenure_months} months`
                                    : 'Not set'}
                                color="#10b981"
                            />
                            <SummaryItem
                                icon="fa-percentage"
                                label="Interest Rate"
                                value={savedData?.interest_rate !== undefined
                                    ? parseFloat(savedData.interest_rate) === 0
                                        ? 'No interest (0%)'
                                        : `${savedData.interest_rate}% per annum`
                                    : 'Not set'}
                                color="#f59e0b"
                            />

                            {savedData?.max_loan_amount > 0 && (
                                <div className="sas-eligibility-box">
                                    <div className="sas-eligibility-title">
                                        <i className="fas fa-chart-bar"></i>
                                        Max Loan Breakdown
                                    </div>
                                    {[1, 2, 3, 4, 5, 6]
                                        .filter((month) => month <= parseInt(savedData.max_tenure_months, 10))
                                        .map((month) => {
                                            const amount = parseFloat(savedData.max_loan_amount);
                                            const rate = parseFloat(savedData.interest_rate) || 0;
                                            const interest = rate > 0
                                                ? (amount * rate * (month / 12)) / 100
                                                : 0;
                                            const emi = (amount + interest) / month;

                                            return (
                                                <div key={month} className="sas-breakdown-row">
                                                    <span className="breakdown-tenure">
                                                        {month} month{month > 1 ? 's' : ''}
                                                    </span>
                                                    <span className="breakdown-emi">
                                                        {RUPEE_SYMBOL}{emi.toLocaleString('en-IN', {
                                                            minimumFractionDigits: 0,
                                                            maximumFractionDigits: 0
                                                        })}/mo
                                                    </span>
                                                </div>
                                            );
                                        })}
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="sas-form-card">
                        <div className="sas-form-header">
                            <i className="fas fa-sliders-h"></i>
                            <div>
                                <h5>Configure Salary Advance</h5>
                                <p>Set limits and interest for employee salary advances</p>
                            </div>
                        </div>

                        <form onSubmit={handleSubmit} className="sas-form-body">
                            <div className="sas-form-group">
                                <label className="sas-label">
                                    Maximum Loan Amount <span className="required">*</span>
                                </label>
                                <div className="sas-input-wrapper">
                                    <div className={`sas-input-box ${errors.max_loan_amount ? 'error' : ''}`}>
                                        <span className="sas-prefix">{RUPEE_SYMBOL}</span>
                                        <input
                                            type="number"
                                            name="max_loan_amount"
                                            value={formData.max_loan_amount}
                                            onChange={handleInputChange}
                                            placeholder="e.g. 50000"
                                            min="1"
                                        />
                                    </div>
                                    {errors.max_loan_amount && (
                                        <span className="sas-error">
                                            <i className="fas fa-exclamation-circle"></i>
                                            {errors.max_loan_amount}
                                        </span>
                                    )}
                                    <p className="sas-hint">
                                        Maximum amount an employee can apply for at one time.
                                    </p>
                                </div>
                            </div>

                            <div className="sas-form-group">
                                <label className="sas-label">
                                    Maximum Tenure <span className="required">*</span>
                                </label>
                                <div className="sas-input-wrapper">
                                    <div className={`sas-tenure-options ${errors.max_tenure_months ? 'error-border' : ''}`}>
                                        {[1, 2, 3, 4, 5, 6].map((month) => (
                                            <button
                                                key={month}
                                                type="button"
                                                className={`tenure-option ${
                                                    parseInt(formData.max_tenure_months, 10) === month ? 'active' : ''
                                                }`}
                                                onClick={() => {
                                                    setFormData((prev) => ({ ...prev, max_tenure_months: String(month) }));
                                                    if (errors.max_tenure_months) {
                                                        setErrors((prev) => ({ ...prev, max_tenure_months: '' }));
                                                    }
                                                }}
                                            >
                                                {month} <span>mo</span>
                                            </button>
                                        ))}
                                    </div>
                                    {errors.max_tenure_months && (
                                        <span className="sas-error">
                                            <i className="fas fa-exclamation-circle"></i>
                                            {errors.max_tenure_months}
                                        </span>
                                    )}
                                    <p className="sas-hint">
                                        Maximum repayment period (1-6 months).
                                    </p>
                                </div>
                            </div>

                            <div className="sas-form-group">
                                <label className="sas-label">Interest Rate (per annum)</label>
                                <div className="sas-input-wrapper">
                                    <div className={`sas-input-box ${errors.interest_rate ? 'error' : ''}`}>
                                        <i className="fas fa-percentage sas-icon"></i>
                                        <input
                                            type="number"
                                            name="interest_rate"
                                            value={formData.interest_rate}
                                            onChange={handleInputChange}
                                            placeholder="0"
                                            step="0.01"
                                            min="0"
                                            max="100"
                                        />
                                        <span className="sas-suffix">% p.a.</span>
                                    </div>
                                    {errors.interest_rate && (
                                        <span className="sas-error">
                                            <i className="fas fa-exclamation-circle"></i>
                                            {errors.interest_rate}
                                        </span>
                                    )}
                                    {parseFloat(formData.interest_rate) === 0 && (
                                        <div className="sas-zero-interest-tag">
                                            <i className="fas fa-gift"></i>
                                            Interest-free loan policy
                                        </div>
                                    )}
                                    <p className="sas-hint">
                                        Set to 0 for an interest-free loan policy.
                                    </p>
                                </div>
                            </div>

                            {emiData && (
                                <div className="sas-emi-preview">
                                    <div className="sas-emi-title">
                                        <i className="fas fa-calculator"></i>
                                        Live EMI Preview
                                    </div>
                                    <div className="sas-emi-grid">
                                        <div className="sas-emi-item">
                                            <span className="emi-item-label">Monthly EMI</span>
                                            <span className="emi-item-value primary">
                                                {RUPEE_SYMBOL}{parseFloat(emiData.simple).toLocaleString('en-IN', {
                                                    minimumFractionDigits: 2
                                                })}
                                            </span>
                                        </div>
                                        <div className="sas-emi-item">
                                            <span className="emi-item-label">Total Payable</span>
                                            <span className="emi-item-value">
                                                {RUPEE_SYMBOL}{parseFloat(emiData.total).toLocaleString('en-IN', {
                                                    minimumFractionDigits: 2
                                                })}
                                            </span>
                                        </div>
                                        {emiData.interest && parseFloat(emiData.interest) > 0 && (
                                            <div className="sas-emi-item">
                                                <span className="emi-item-label">Total Interest</span>
                                                <span className="emi-item-value orange">
                                                    {RUPEE_SYMBOL}{parseFloat(emiData.interest).toLocaleString('en-IN', {
                                                        minimumFractionDigits: 2
                                                    })}
                                                </span>
                                            </div>
                                        )}
                                        <div className="sas-emi-item">
                                            <span className="emi-item-label">Tenure</span>
                                            <span className="emi-item-value">
                                                {formData.max_tenure_months} month{formData.max_tenure_months > 1 ? 's' : ''}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="sas-form-footer">
                                <button
                                    type="submit"
                                    className="btn-sas-save"
                                    disabled={saving}
                                >
                                    {saving ? (
                                        <>
                                            <span className="btn-spinner"></span>
                                            Saving...
                                        </>
                                    ) : (
                                        <>
                                            <i className="fas fa-save"></i>
                                            Update Settings
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </MainLayout>
    );
}

function SummaryItem({ icon, label, value, color }) {
    return (
        <div className="sas-summary-row">
            <div className="sas-summary-icon" style={{ background: `${color}18`, color }}>
                <i className={`fas ${icon}`}></i>
            </div>
            <div className="sas-summary-text">
                <span className="sas-summary-label">{label}</span>
                <span className="sas-summary-value">{value}</span>
            </div>
        </div>
    );
}

export default SalaryAdvanceSettings;
